import getSceneObjectMotionEditJob from '@cartwhl/common/api/getSceneObjectMotionEditJob';
import postSceneObjectMotionEdit from '@cartwhl/common/api/postSceneObjectMotionEdit';
import type { MotionEditorJobResponse } from '@cartwhl/common/api/postSceneObjectMotionEdit';
import postSceneObjectMotionEditApply from '@cartwhl/common/api/postSceneObjectMotionEditApply';

import { getErrorMessage, wait } from '../keyPoses/keyPoseStoreUtils';
import type { MotionEditorStore } from '../motionEditorStore';
import type {
  RepathingGenerationPhase,
  RepathingGenerationResult,
  RepathingRoot2dConstraint,
  RepathingState,
} from './types';

const REPATHING_GENERATION_POLL_INTERVAL_MS = 1500;

export type RepathingGenerationActionDeps = {
  parentStore: MotionEditorStore;
  state: RepathingState;
};

export interface RegenerateSceneObjectMotionPathInput {
  sceneID: string;
  referenceName: string;
  motionID: string;
  timelineIndex: number;
  constraint: RepathingRoot2dConstraint;
  apiKey: string;
  prompt?: string;
  signal?: AbortSignal;
}

export async function regenerateSceneObjectMotionPath(
  deps: RepathingGenerationActionDeps,
  {
    sceneID,
    referenceName,
    motionID,
    timelineIndex,
    constraint,
    apiKey,
    prompt,
    signal,
  }: RegenerateSceneObjectMotionPathInput,
): Promise<RepathingGenerationResult> {
  const { state } = deps;
  if (state.generation.status === 'pending') {
    return { ok: false, error: 'Regeneration is already in progress.' };
  }

  const runId = createRepathingGenerationRunId(referenceName, motionID);
  startGeneration(state, runId);

  const { data, error } = await postSceneObjectMotionEdit(
    sceneID,
    referenceName,
    timelineIndex,
    {
      prompt,
      constraints: [constraint],
    },
    apiKey,
    signal,
  );

  if (error || !data) {
    const errorMessage = getErrorMessage(error, 'Failed to regenerate motion.');
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const completedJob = await waitForSceneObjectMotionEditJob(deps, {
    apiKey,
    initialJob: data,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  });
  if (!completedJob) {
    return {
      ok: false,
      error: state.generation.error ?? 'Failed to regenerate motion.',
    };
  }

  if (!completedJob.outputBvhURL) {
    const errorMessage = 'Regeneration did not return a BVH URL.';
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const applyResult = await applyGeneratedPathMotionToScene(deps, {
    apiKey,
    job: completedJob,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  });

  if (!applyResult.ok) {
    return applyResult;
  }

  finishGeneration(state, runId, completedJob);
  return { ok: true, job: completedJob };
}

function startGeneration(
  state: RepathingState,
  runId: string,
  phase: RepathingGenerationPhase = 'queued',
) {
  state.generation = {
    status: 'pending',
    phase,
    activeRunId: runId,
    error: null,
  };
}

function setGenerationPhase(
  state: RepathingState,
  runId: string,
  phase: RepathingGenerationPhase,
) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation.phase = phase;
}

function finishGeneration(
  state: RepathingState,
  runId: string,
  job: MotionEditorJobResponse,
) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation = {
    status: 'complete',
    phase: null,
    activeRunId: null,
    error: null,
    completedJobId: job.jobID,
    outputBvhURL: job.outputBvhURL,
  };
}

function failGeneration(state: RepathingState, runId: string, error: string) {
  if (state.generation.activeRunId !== runId) {
    return;
  }

  state.generation = {
    status: 'failed',
    phase: null,
    activeRunId: null,
    error,
  };
}

async function waitForSceneObjectMotionEditJob(
  deps: RepathingGenerationActionDeps,
  {
    apiKey,
    initialJob,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  }: {
    apiKey: string;
    initialJob: MotionEditorJobResponse;
    referenceName: string;
    runId: string;
    sceneID: string;
    signal?: AbortSignal;
    timelineIndex: number;
  },
) {
  const { state } = deps;
  let job = initialJob;

  for (;;) {
    setGenerationPhase(
      state,
      runId,
      getGenerationPhaseForJobStatus(job.status),
    );

    if (job.status === 'COMPLETED') {
      return job;
    }

    if (job.status === 'FAILED' || job.status === 'CANCELLED') {
      failGeneration(
        state,
        runId,
        job.errorMessage || `Motion edit ${job.status.toLowerCase()}.`,
      );
      return null;
    }

    await wait(REPATHING_GENERATION_POLL_INTERVAL_MS, signal);
    if (signal?.aborted) {
      failGeneration(state, runId, 'Regeneration was cancelled.');
      return null;
    }

    const { data, error } = await getSceneObjectMotionEditJob(
      sceneID,
      referenceName,
      timelineIndex,
      job.jobID,
      apiKey,
      signal,
    );

    if (error || !data) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to poll regeneration job.',
      );
      failGeneration(state, runId, errorMessage);
      return null;
    }

    job = data;
  }
}

async function applyGeneratedPathMotionToScene(
  deps: RepathingGenerationActionDeps,
  {
    apiKey,
    job,
    referenceName,
    runId,
    sceneID,
    signal,
    timelineIndex,
  }: {
    apiKey: string;
    job: MotionEditorJobResponse;
    referenceName: string;
    runId: string;
    sceneID: string;
    signal?: AbortSignal;
    timelineIndex: number;
  },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { parentStore, state } = deps;
  setGenerationPhase(state, runId, 'updating-timeline');

  const { error: applyError } = await postSceneObjectMotionEditApply(
    sceneID,
    referenceName,
    timelineIndex,
    job.jobID,
    apiKey,
    signal,
  );

  if (applyError) {
    const errorMessage = getErrorMessage(
      applyError,
      'Failed to update timeline.',
    );
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  const didRefreshScene = await parentStore.refreshSceneAfterTimelineMutation({
    apiKey,
    referenceName,
    sceneID,
    timelineIndex,
  });
  if (!didRefreshScene) {
    const errorMessage = 'Failed to refresh updated timeline.';
    failGeneration(state, runId, errorMessage);
    return { ok: false, error: errorMessage };
  }

  return { ok: true };
}

function getGenerationPhaseForJobStatus(
  status: MotionEditorJobResponse['status'],
): RepathingGenerationPhase {
  return status === 'QUEUED' ? 'queued' : 'regenerating';
}

function createRepathingGenerationRunId(
  referenceName: string,
  motionID: string,
) {
  const randomID = globalThis.crypto?.randomUUID?.() ?? String(Date.now());
  return `${referenceName}:${motionID}:path:${randomID}`;
}
